import {
  Box,
  Grid,
  Pagination,
  Stack,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';
import ProviderChip from 'components/ProviderChip';
import { apiSlice } from 'features/api/apiSlice';
import { getValue, normalizeCursorPage, toNumber } from 'helpers/PatientHealthDataHelper';
import PatientMetricCard from 'pages/patients/tabs/PatientMetricCard';
import PatientDateRangeSelector from 'pages/patients/tabs/PatientDateRangeSelector';
import type { PatientDateRange } from 'pages/patients/tabs/PatientDateRangeSelector';
import { Activity, Armchair, Flame, Footprints, Heart, MoveHorizontal, Timer, TrendingUp } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import type { ApexOptions } from 'apexcharts';
import ApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { store } from 'store';
import i18n from 'app/i18n';

export type ActivityDayRange = PatientDateRange;

type ActivityRecord = Record<string, unknown>;

export function getActivityDateRange(range: ActivityDayRange): { start: moment.Moment; end: moment.Moment } {
  return {
    start: moment().subtract(range, 'days').startOf('day'),
    end: moment().endOf('day')
  };
}

type ActivitySummaryTotals = {
  count: number;
  totalSteps: number;
  totalActiveCalories: number;
  totalActiveMinutes: number;
  totalDistance: number;
  totalFloorsClimbed: number;
  totalSedentaryMinutes: number;
  avgHeartRate: number;
  heartRateSamples: number;
  heartRateTotal: number;
};

type ActivityMetricKey =
  | 'steps'
  | 'active_calories'
  | 'active_minutes'
  | 'average_heart_rate'
  | 'distance'
  | 'floors_climbed'
  | 'sedentary_minutes'
  | 'days_tracked';

interface ActivityTabProps {
  userId: string;
}

function formatMetricAxisValue(value: number, metric: ActivityMetricKey): string {
  switch (metric) {
    case 'distance':
      return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : `${(value / 1000).toFixed(1)}k`;
    case 'active_minutes':
    case 'sedentary_minutes':
      return `${Math.round(value / 60)}h`;
    case 'average_heart_rate':
      return `${Math.round(value)}`;
    default:
      return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : `${Math.round(value)}`;
  }
}

function formatMetricTooltipValue(value: number, metric: ActivityMetricKey): string {
  switch (metric) {
    case 'distance':
      return `${(value / 1000).toFixed(1)} km`;
    case 'active_minutes':
    case 'sedentary_minutes':
      return `${Math.round(value)} min`;
    case 'average_heart_rate':
      return `${Math.round(value)} bpm`;
    case 'active_calories':
      return `${Math.round(value)} kcal`;
    default:
      return `${Math.round(value)}`;
  }
}

function getMetricValue(record: ActivityRecord, metric: ActivityMetricKey): number {
  switch (metric) {
    case 'steps':
      return toNumber(getValue(record, ['steps'])) ?? 0;
    case 'active_calories':
      return toNumber(getValue(record, ['active_calories_kcal'])) ?? 0;
    case 'active_minutes':
      return toNumber(getValue(record, ['active_minutes'])) ?? 0;
    case 'average_heart_rate':
      return getHeartRateAvg(record) ?? 0;
    case 'distance':
      return toNumber(getValue(record, ['distance_meters'])) ?? 0;
    case 'floors_climbed':
      return toNumber(getValue(record, ['floors_climbed'])) ?? 0;
    case 'sedentary_minutes':
      return toNumber(getValue(record, ['sedentary_minutes'])) ?? 0;
    default:
      return 0;
  }
}

function getHeartRateAvg(record: ActivityRecord): number | null {
  const hr = getValue(record, ['heart_rate']);
  if (hr && typeof hr === 'object') {
    const hrStats = hr as { avg_bpm?: unknown };
    return toNumber(hrStats.avg_bpm);
  }
  return toNumber(getValue(record, ['avg_heart_rate_bpm', 'avg_heart_rate']));
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '0.0 km';
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatHoursMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '0h 0m';

  const duration = moment.duration(totalMinutes, 'minutes');
  return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
}

function useActivitySummaryData(userId: string, startOfRange: moment.Moment, endOfRange: moment.Moment) {
  const [records, setRecords] = useState<ActivityRecord[]>([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadRecords = async () => {
      setIsFetching(true);

      const collected: ActivityRecord[] = [];
      let nextCursor: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const result: { data?: unknown } = await store.dispatch(
          apiSlice.endpoints.getUserActivitySummaries.initiate({
            userId,
            from: startOfRange.toISOString(),
            to: endOfRange.toISOString(),
            limit: 100,
            cursor: nextCursor
          })
        );

        if ('data' in result) {
          const { items: pageData, pagination } = normalizeCursorPage<ActivityRecord>(result.data);
          collected.push(...pageData);

          const nextPageCursor = pagination?.next_cursor ?? undefined;
          hasMore = Boolean(pagination?.has_more) && Boolean(nextPageCursor);
          nextCursor = nextPageCursor;

          if (!pagination?.has_more || !nextPageCursor) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }

        const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      }

      if (isActive) {
        setRecords(collected);
        setIsFetching(false);
      }
    };

    void loadRecords();

    return () => {
      isActive = false;
    };
  }, [userId, startOfRange, endOfRange]);

  return { records, isFetching };
}

export default function ActivityTab({ userId }: ActivityTabProps) {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState<ActivityDayRange>(30);
  const [selectedMetric, setSelectedMetric] = useState<ActivityMetricKey>('steps');
  const { start: startOfRange, end: endOfRange } = useMemo(() => getActivityDateRange(selectedRange), [selectedRange]);

  const { records, isFetching } = useActivitySummaryData(userId, startOfRange, endOfRange);
  const filteredRecords: ActivityRecord[] = useMemo(() => {
    return records.filter((record) => {
      const dateValue = getValue(record, ['date']);
      if (!dateValue) return false;
      const d = moment(String(dateValue));
      return d.isValid() && d.isBetween(startOfRange, endOfRange, 'day', '[]');
    });
  }, [selectedRange, records]);

  const totals: ActivitySummaryTotals = useMemo(() => {
    const summary = filteredRecords.reduce<ActivitySummaryTotals>(
      (acc, record) => {
        acc.count += 1;
        acc.totalSteps += toNumber(getValue(record, ['steps'])) ?? 0;
        acc.totalActiveCalories += toNumber(getValue(record, ['active_calories_kcal'])) ?? 0;
        acc.totalActiveMinutes += toNumber(getValue(record, ['active_minutes'])) ?? 0;
        acc.totalDistance += toNumber(getValue(record, ['distance_meters'])) ?? 0;
        acc.totalFloorsClimbed += toNumber(getValue(record, ['floors_climbed'])) ?? 0;
        acc.totalSedentaryMinutes += toNumber(getValue(record, ['sedentary_minutes'])) ?? 0;

        const avgHr = getHeartRateAvg(record);
        if (avgHr !== null && avgHr > 0) {
          acc.heartRateTotal += avgHr;
          acc.heartRateSamples += 1;
        }

        return acc;
      },
      {
        count: 0,
        totalSteps: 0,
        totalActiveCalories: 0,
        totalActiveMinutes: 0,
        totalDistance: 0,
        totalFloorsClimbed: 0,
        totalSedentaryMinutes: 0,
        avgHeartRate: 0,
        heartRateSamples: 0,
        heartRateTotal: 0
      }
    );

    return {
      ...summary,
      avgHeartRate: summary.heartRateSamples > 0 ? summary.heartRateTotal / summary.heartRateSamples : 0
    };
  }, [filteredRecords]);

  const summaryMetrics = useMemo(
    () => [
      {
        key: 'steps' as ActivityMetricKey,
        label: t('steps', 'Steps'),
        accent: '#22c55e',
        bgColor: 'rgba(34, 197, 94, 0.12)',
        icon: Footprints,
        value: totals.totalSteps
      },
      {
        key: 'active_calories' as ActivityMetricKey,
        label: t('active_calories', 'Active Calories'),
        accent: '#f97316',
        bgColor: 'rgba(249, 115, 22, 0.12)',
        icon: Flame,
        value: Math.round(totals.totalActiveCalories)
      },
      {
        key: 'active_minutes' as ActivityMetricKey,
        label: t('active_minutes', 'Active Time'),
        accent: '#0ea5e9',
        bgColor: 'rgba(14, 165, 233, 0.12)',
        icon: Timer,
        value: formatHoursMinutes(totals.totalActiveMinutes)
      },
      {
        key: 'average_heart_rate' as ActivityMetricKey,
        label: t('avg_heart_rate', 'Average Heart Rate'),
        accent: '#f43f5e',
        bgColor: 'rgba(244, 63, 94, 0.12)',
        icon: Heart,
        value: totals.heartRateSamples > 0 ? `${Math.round(totals.avgHeartRate)} bpm` : '—'
      },
      {
        key: 'distance' as ActivityMetricKey,
        label: t('distance', 'Distance'),
        accent: '#a855f7',
        bgColor: 'rgba(168, 85, 247, 0.12)',
        icon: MoveHorizontal,
        value: formatDistance(totals.totalDistance)
      },
      {
        key: 'floors_climbed' as ActivityMetricKey,
        label: t('floors_climbed', 'Floors Climbed'),
        accent: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.12)',
        icon: TrendingUp,
        value: totals.totalFloorsClimbed.toLocaleString()
      },
      {
        key: 'sedentary_minutes' as ActivityMetricKey,
        label: t('sedentary_time', 'Sedentary Time'),
        accent: '#71717a',
        bgColor: 'rgba(113, 113, 122, 0.12)',
        icon: Armchair,
        value: formatHoursMinutes(totals.totalSedentaryMinutes)
      },
      {
        key: 'days_tracked' as ActivityMetricKey,
        label: t('days_tracked', 'Days Tracked'),
        accent: '#6366f1',
        bgColor: 'rgba(99, 102, 241, 0.12)',
        icon: Activity,
        value: totals.count.toLocaleString(),
        isStatic: true
      }
    ],
    [t, totals]
  );

  const selectedMetricAccent = useMemo(
    () => summaryMetrics.find((metric) => metric.key === selectedMetric)?.accent ?? '#6366f1',
    [summaryMetrics, selectedMetric]
  );

  const chartData = useMemo(() => {
    return filteredRecords
      .map((record) => {
        const dateValue = getValue(record, ['date']);
        if (!dateValue) return null;
        const d = moment(String(dateValue));
        if (!d.isValid()) return null;

        return {
          label: d.locale(i18n.language).format('MMM D'),
          value: getMetricValue(record, selectedMetric)
        };
      })
      .filter(
        (entry): entry is { label: string; value: number } =>
          entry !== null && Number.isFinite(entry.value)
      )
      .sort((a, b) => moment(a.label, 'MMM D').valueOf() - moment(b.label, 'MMM D').valueOf());
  }, [filteredRecords, selectedMetric]);

  const chartSeriesName =
    selectedMetric === 'steps'
      ? t('daily_steps', 'Daily Steps')
      : selectedMetric === 'active_calories'
        ? t('daily_active_calories', 'Daily Active Calories')
        : selectedMetric === 'active_minutes'
          ? t('daily_active_minutes', 'Daily Active Time')
          : selectedMetric === 'average_heart_rate'
            ? t('daily_avg_heart_rate', 'Daily Average Heart Rate')
            : selectedMetric === 'distance'
              ? t('daily_distance', 'Daily Distance')
              : selectedMetric === 'floors_climbed'
                ? t('daily_floors_climbed', 'Daily Floors Climbed')
                : t('daily_sedentary_minutes', 'Daily Sedentary Minutes');

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar' as const,
        toolbar: { show: false },
        background: 'transparent'
      },
      colors: [selectedMetricAccent],
      plotOptions: {
        bar: {
          borderRadius: 6,
          borderRadiusApplication: 'end' as const
        }
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: 'rgba(148, 163, 184, 0.15)',
        strokeDashArray: 3
      },
      tooltip: {
        theme: 'dark',
        y: {
          formatter: (value: number) => formatMetricTooltipValue(value, selectedMetric)
        }
      },
      xaxis: {
        categories: chartData.map((item) => item.label),
        labels: {
          style: {
            colors: '#94a3b8',
            fontSize: '11px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (value: number) => formatMetricAxisValue(value, selectedMetric),
          style: {
            colors: '#94a3b8',
            fontSize: '11px'
          }
        }
      },
      stroke: { show: false }
    } satisfies ApexOptions),
    [chartData, selectedMetric, selectedMetricAccent]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCache, setPageCache] = useState<Record<number, { items: ActivityRecord[]; nextCursor: string | null }>>({});
  const [pageLoading, setPageLoading] = useState(false);

  const currentPageData = pageCache[currentPage] ?? { items: [], nextCursor: null };
  const hasNextPage = Boolean(currentPageData.nextCursor);

  useEffect(() => {
    setCurrentPage(1);
    setPageCache({});
    setPageLoading(false);
  }, [userId]);

  useEffect(() => {
    const page = currentPage;

    if (pageCache[page]) {
      setPageLoading(false);
      return;
    }

    let isActive = true;
    const cursor = page === 1 ? undefined : pageCache[page - 1]?.nextCursor ?? undefined;

    if (page > 1 && !cursor) {
      return;
    }

    const loadPage = async () => {
      setPageLoading(true);

      const result = await store.dispatch(
        apiSlice.endpoints.getUserActivitySummaries.initiate({
          userId,
          from: moment(0).toISOString(),
          to: moment().endOf('day').toISOString(),
          limit: 10,
          cursor,
          sortOrder: 'desc'
        })
      );

      if ('data' in result) {
        const { items, pagination } = normalizeCursorPage<ActivityRecord>(result.data);
        const nextCursor = pagination?.next_cursor ?? null;

        if (isActive) {
          setPageCache((prev) => ({ ...prev, [page]: { items, nextCursor } }));
        }
      }

      if (isActive) {
        setPageLoading(false);
      }

      const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };

    void loadPage();

    return () => {
      isActive = false;
    };
  }, [currentPage, userId, pageCache]);

  function getRecordSource(record: ActivityRecord): string | null {
    const source = getValue(record, ['source']);
    if (!source) return null;
    if (typeof source === 'string' && source.trim()) return source.trim();
    if (typeof source === 'object' && source !== null) {
      const s = source as { provider?: unknown; source?: unknown; name?: unknown };
      const provider = typeof s.provider === 'string' ? s.provider.trim() : '';
      const sourceName = typeof s.source === 'string' ? s.source.trim() : '';
      if (provider) return provider;
      if (sourceName) return sourceName;
    }
    return null;
  }

  function ActivityRow({ record }: { record: ActivityRecord }) {
    const steps = toNumber(getValue(record, ['steps'])) ?? 0;
    const activeCalories = toNumber(getValue(record, ['active_calories_kcal'])) ?? 0;
    const activeMinutes = toNumber(getValue(record, ['active_minutes'])) ?? 0;
    const distance = toNumber(getValue(record, ['distance_meters'])) ?? 0;
    const floorsClimbed = toNumber(getValue(record, ['floors_climbed'])) ?? 0;
    const sedentaryMinutes = toNumber(getValue(record, ['sedentary_minutes'])) ?? 0;
    const avgHr = getHeartRateAvg(record);
    const source = getRecordSource(record);

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            textAlign: 'left'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 2 }}>
            <Box sx={{ width: 132, flexShrink: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {moment(String(getValue(record, ['date']))).isValid()
                  ? moment(String(getValue(record, ['date']))).locale(i18n.language).format('ddd, MMM D')
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(String(getValue(record, ['date']))).isValid()
                  ? moment(String(getValue(record, ['date']))).locale(i18n.language).format('YYYY')
                  : '—'}
              </Typography>

              {source && (
                <Box sx={{ pt: 1 }}>
                  <ProviderChip provider={source} />
                </Box>
              )}
            </Box>

            <Grid container spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Footprints size={16} strokeWidth={1.8} style={{ color: '#22c55e', flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{steps > 0 ? steps.toLocaleString() : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('steps', 'Steps')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Flame size={16} strokeWidth={1.8} style={{ color: '#f97316' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{activeCalories > 0 ? `${Math.round(activeCalories).toLocaleString()} kcal` : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('calories', 'Calories')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Timer size={16} strokeWidth={1.8} style={{ color: '#0ea5e9', flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{activeMinutes > 0 ? formatHoursMinutes(activeMinutes) : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('active_time', 'Active Time')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Heart size={16} strokeWidth={1.8} style={{ color: '#f43f5e' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{avgHr && avgHr > 0 ? `${Math.round(avgHr)} bpm` : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('avg_hr', 'Average HR')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <MoveHorizontal size={16} strokeWidth={1.8} style={{ color: '#a855f7', flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{distance > 0 ? formatDistance(distance) : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('distance', 'Distance')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <TrendingUp size={16} strokeWidth={1.8} style={{ color: '#f59e0b' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{floorsClimbed > 0 ? floorsClimbed.toLocaleString() : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('floors_climbed', 'Floors')}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12/7}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Armchair size={16} strokeWidth={1.8} style={{ color: '#71717a' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{sedentaryMinutes > 0 ? formatHoursMinutes(sedentaryMinutes) : '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('sedentary_time', 'Sedentary')}</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <MainCard
        title={t('summary', 'Summary')}
        secondary={
          <PatientDateRangeSelector value={selectedRange} onChange={setSelectedRange} />
        }
      >
        {isFetching ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <Grid item xs={12} sm={6} lg={3} key={item}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'action.hover' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ width: '42%', height: 28, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ width: '60%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredRecords.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">{t('no_activity_data_in_period', 'No activity data in this period')}</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {summaryMetrics.map((metric) => {
                const isSelected = !metric.isStatic && selectedMetric === metric.key;

                return (
                  <Grid item xs={12} sm={6} lg={3} key={metric.label}>
                    <PatientMetricCard
                      icon={metric.icon}
                      accent={metric.accent}
                      bgColor={metric.bgColor}
                      value={metric.value}
                      label={metric.label}
                      selected={isSelected}
                      selectable={!metric.isStatic}
                      onSelect={() => setSelectedMetric(metric.key)}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {chartData.length > 1 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {selectedMetric === 'steps'
                    ? t('daily_steps', 'Daily Steps')
                    : selectedMetric === 'active_calories'
                      ? t('daily_active_calories', 'Daily Active Calories')
                      : selectedMetric === 'active_minutes'
                        ? t('daily_active_minutes', 'Daily Active Time')
                        : selectedMetric === 'average_heart_rate'
                          ? t('daily_avg_heart_rate', 'Daily Average Heart Rate')
                          : selectedMetric === 'distance'
                            ? t('daily_distance', 'Daily Distance')
                            : selectedMetric === 'floors_climbed'
                              ? t('daily_floors_climbed', 'Daily Floors Climbed')
                              : t('daily_sedentary_minutes', 'Daily Sedentary Minutes')}
                </Typography>
                <ApexChart
                  options={chartOptions}
                  series={[{ name: chartSeriesName, data: chartData.map((item) => item.value) }]}
                  type="bar"
                  height={220}
                />
              </Box>
            )}
          </>
        )}
      </MainCard>

      <MainCard
        title={t('all_activities', 'All Activities')}
        secondary={
          <Typography variant="caption" color="text.secondary">
            {t('page', 'Page')} {currentPage}
          </Typography>
        }
      >
        <Stack spacing={1.5}>
          {pageLoading ? (
            <Stack spacing={1}>
              {[1, 2, 3, 4].map((item) => (
                <Box
                  key={item}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
                    <Box sx={{ width: 132, flexShrink: 0 }}>
                      <Box sx={{ width: 112, height: 16, borderRadius: 1, bgcolor: 'action.hover', mb: 0.75 }} />
                      <Box sx={{ width: 72, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ mt: 1, width: 76, height: 28, borderRadius: 999, bgcolor: 'action.hover' }} />
                    </Box>

                    <Grid container spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5, 6].map((metricItem) => (
                        <Grid item xs={12} sm={12 / 6} key={metricItem}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center' }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'action.hover', flexShrink: 0 }} />
                            <Box>
                              <Box sx={{ width: 60, height: 16, borderRadius: 1, bgcolor: 'action.hover', mb: 0.5 }} />
                              <Box sx={{ width: 44, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                            </Box>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : currentPageData.items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">{t('no_activity_data', 'No activity data found.')}</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {currentPageData.items.map((record, idx) => (
                <ActivityRow key={idx} record={record} />
              ))}
            </Stack>
          )}

          <Stack spacing={2} sx={{ pt: 1.5 }} alignItems="flex-end">
            <Pagination
              count={Math.max(1, currentPage + (hasNextPage ? 1 : 0))}
              size="medium"
              page={currentPage}
              variant="combined"
              color="primary"
              onChange={(_, page) => {
                if (!pageLoading && page !== currentPage) {
                  setCurrentPage(page);
                }
              }}
            />
          </Stack>
        </Stack>
      </MainCard>
    </Stack>
  );
}
